import React, { useEffect, useState, useCallback, useMemo } from "react";
import { LocalDate, Month } from "@js-joda/core";
import { useTranslation } from "react-i18next";
import { Color } from "config/colors";
import { FontFamily, FontSize } from "config/font";
import {
  MonthSelectContainer,
  ArrowSign,
  WorkerTimeReportContainer,
  PersonnelCarrierWrapper,
  HeadCell,
  PersonnelCarrierGrid,
  PersonnelCarrierGridBody,
  Cell,
  IconContainer,
  TextRowPersonnelCarrier,
} from "./monthlyOverview.styled";
import { Text, H4 } from "components/Typography/text.styled";
import { useTimeReportStore } from "web-apps/company/stores/timeReportStore/timeReportStore";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { Api } from "services/api/api.service";
import { CustomIcon } from "components/icon/customIcon.component";
import { PersonnelCarrierFilterModal } from "./personnelCarrierFilterModal";
import { TimeReportResolution, TimeReportStatus } from "model/TimeReport";
import { isWithinXMinutes } from "utils/utils";
import { CompanyWorkerDto } from "model/Company";
import { fetchWorker } from "web-apps/company/utils/utils";
import { WorkerDetail } from "./workers/workerDetail.component";

export const PersonnelCarrierOverview: React.FC = () => {
  const { t } = useTranslation();
  const [timeReportState, , timeReportDispatch] = useTimeReportStore();
  const [companyState] = useCompanyStore();

  //states
  const [isLoading, setIsLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState<LocalDate>(LocalDate.now());
  const [monthsDates, setMonthsDates] = useState<string[]>([]);
  const [workerModalOpen, setWorkerModalOpen] = useState(false);
  const [worker, setWorker] = useState<CompanyWorkerDto>();
  const [noTimeReports, setNoTimeReports] = useState(false);
  const [chosenMonth, setChosenMonth] = useState<Month>(
    LocalDate.now().month()
  );

  // Filter states
  const [showOnlyDaysWithGigs, setShowOnlyDaysWithGigs] = useState(false);
  const [showTodaysGigs, setShowTodaysGigs] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showTodaysGigsMarked, setShowTodaysGigsMarked] = useState(false);
  const [showOnlyDaysWithGigsMarked, setShowOnlyDaysWithGigsMarked] =
    useState(false);

  const year = currentDate.year();

  const getDatesInMonth = (month: Month, year: number) => {
    let dates = [];

    const leapYear = new Date(year, 1, 29).getMonth() === 1;
    const daysInMonth = Month.of(new Date().getMonth() + 1).equals(month)
      ? new Date().getDate()
      : month.length(leapYear);

    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month.value() - 1, i);

      const formattedDate = `${date.getFullYear()}-${String(
        date.getMonth() + 1
      ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      dates.push(formattedDate);
    }

    return dates;
  };

  const checkForValidResolution = (resolution: any) => {
    return (
      resolution !== TimeReportResolution.SICKLEAVE &&
      resolution !== TimeReportResolution.SICKLEAVECHILD &&
      resolution !== TimeReportResolution.VACATION &&
      resolution !== TimeReportResolution.PARENTALLEAVE &&
      resolution !== TimeReportResolution.LEAVEOFABSENCE &&
      resolution !== TimeReportResolution.OVERTIMECOMPENSATION &&
      resolution !== TimeReportResolution.FUNERALLEAVE &&
      resolution !== TimeReportResolution.MILITARYSERVICE &&
      resolution !== TimeReportResolution.WITHDRAWN &&
      resolution !== TimeReportResolution.CANCELLED
    );
  };

  const checkForFilter = (date: string | number | Date) => {
    return timeReportState.personnelCarrierTimeReports.some(
      (workerWithReports) => {
        return workerWithReports.stamps.some((report) => {
          const reportDate = new Date(report.startTime)
            .toISOString()
            .split("T")[0];
          const targetDate = new Date(date).toISOString().split("T")[0];
          return (
            reportDate === targetDate &&
            checkForValidResolution(report.resolution)
          );
        });
      }
    );
  };

  const checkIfThereAreRejectedOrClosedTimeReports = (status: string) => {
    return timeReportState.personnelCarrierTimeReports.some(
      (workerWithReports) => {
        return workerWithReports.stamps.some((report) => {
          return report.status == status;
        });
      }
    );
  };

  const getMonthlyTimeReports = useCallback(async () => {
    setIsLoading(true);
    const companyId = companyState.company?.id;
    if (companyId) {
      let day;

      if (Month.of(new Date().getMonth() + 1).equals(chosenMonth)) {
        day = new Date().getDate();
      } else {
        const leapYear = new Date(year, 1, 29).getMonth() === 1;
        day = chosenMonth.length(leapYear);
      }

      try {
        const res = await Api().company.timeReport.getAllWorkerPersonnelCarrier(
          companyId,
          currentDate.year(),
          currentDate.monthValue(),
          day
        );

        const monthlyList = res.data;
        setIsLoading(false);
        timeReportDispatch({
          type: "UPDATE_MONTHLY_CARRIER_TIME_REPORTS",
          payload: monthlyList,
        });
      } catch (err) {
        console.log(err, "ERROR FETCHING MONTHLY TIME REPORTS", err);
        setIsLoading(false);
      }
    }
  }, [
    companyState.company?.id,
    timeReportDispatch,
    chosenMonth,
    year,
    currentDate,
  ]);

  useEffect(() => {
    getMonthlyTimeReports();
  }, [getMonthlyTimeReports]);

  useEffect(() => {
    setMonthsDates(getDatesInMonth(chosenMonth, year));
  }, [chosenMonth, year]);

  useEffect(() => {
    setChosenMonth(currentDate.month());
  }, [currentDate]);

  useEffect(() => {
    if (worker) {
      setWorkerModalOpen(true);
    }
  }, [worker]);

  useEffect(() => {
    const reportExists = checkForFilter(currentDate.toString());
    !reportExists && showTodaysGigs
      ? setNoTimeReports(true)
      : setNoTimeReports(false);
  }, [showTodaysGigs, timeReportState, currentDate]);

  useEffect(() => {
    const reportExists =
      timeReportState.personnelCarrierTimeReports.length !== 0;

    !reportExists && showOnlyDaysWithGigs
      ? setNoTimeReports(true)
      : setNoTimeReports(false);
  }, [showOnlyDaysWithGigs, timeReportState]);

  useEffect(() => {
    if (showTodaysGigs) {
      setCurrentDate(currentDate.withMonth(LocalDate.now().month()));
    }
  }, [showTodaysGigs, currentDate]);

  function areSameDaysInSwedishTimezone(date1: Date, date2: Date) {
    const options = { timeZone: "Europe/Stockholm" };

    const d1 = new Intl.DateTimeFormat("sv-SE", {
      ...options,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date1);

    const d2 = new Intl.DateTimeFormat("sv-SE", {
      ...options,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date2);

    return d1 === d2;
  }

  const formatTime = (date: any) => {
    return new Date(date).toLocaleTimeString("sv-SE", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const haveStampClockModule = useMemo(() => {
    return (
      companyState.companySettings?.find(
        (s: any) => s.key === "CompanyShowStampclockModule"
      )?.value == "true"
    );
  }, [companyState.companySettings]);

  const shouldDisplayDate = (date: string, options: any) => {
    const {
      showTodaysGigs,
      showOnlyDaysWithGigs,
      currentDate,
      checkForFilter,
      noTimeReports,
    } = options;

    const allFilters =
      showTodaysGigs &&
      showOnlyDaysWithGigs &&
      date == currentDate.toString() &&
      checkForFilter(currentDate.toString());

    const noFilters = !showOnlyDaysWithGigs && !showTodaysGigs;

    const showTodaysGigsFilter =
      showTodaysGigs &&
      date == currentDate.toString() &&
      !showOnlyDaysWithGigs &&
      !noTimeReports;

    const showOnlyDaysWithGigsFilter =
      showOnlyDaysWithGigs && checkForFilter(date) && !showTodaysGigs;

    return (
      allFilters ||
      noFilters ||
      showTodaysGigsFilter ||
      showOnlyDaysWithGigsFilter
    );
  };

  const handleWorkerClick = async (workerId: number) => {
    try {
      const workerData = await fetchWorker(workerId, companyState);
      setWorker(workerData.data);
    } catch (error) {
      console.error("Failed to fetch worker details", error);
    }
  };

  const renderPersonnelCarrierRow = (date: string) => {
    return (
      <PersonnelCarrierGridBody haveStampClockModule={haveStampClockModule}>
        {!isLoading &&
          timeReportState.personnelCarrierTimeReports.map(
            (workerWithReports) => {
              return workerWithReports.stamps.map((report) => {
                if (
                  areSameDaysInSwedishTimezone(
                    new Date(report.startTime),
                    new Date(date)
                  ) &&
                  checkForValidResolution(report.resolution)
                ) {
                  return (
                    <React.Fragment key={report.timeReportId}>
                      <Cell>
                        <Text
                          fontSize={FontSize.Large}
                          onClick={() =>
                            handleWorkerClick(workerWithReports.workerId)
                          }
                        >
                          {workerWithReports.firstName +
                            " " +
                            workerWithReports.lastName}
                        </Text>
                      </Cell>
                      <Cell>
                        <Text fontSize={FontSize.Large}>
                          {workerWithReports.ssn}
                        </Text>
                      </Cell>
                      <Cell>
                        <Text fontSize={FontSize.Large}>
                          {formatTime(report.startTime)}
                        </Text>
                      </Cell>
                      <Cell>
                        <Text fontSize={FontSize.Large}>
                          {formatTime(report.endTime)}
                        </Text>
                      </Cell>
                      <Cell>
                        <Text fontSize={FontSize.Large}>
                          {report.breakTimeMinutes + " min"}
                        </Text>
                      </Cell>
                      {haveStampClockModule && (
                        <>
                          <Cell>
                            <Text
                              fontSize={FontSize.Large}
                              color={
                                report.checkInTime == null ||
                                isWithinXMinutes(
                                  new Date(report.startTime),
                                  new Date(report.checkInTime),
                                  15
                                )
                                  ? undefined
                                  : Color.Destructive
                              }
                            >
                              {report.checkInTime
                                ? formatTime(report.checkInTime)
                                : "-"}
                            </Text>
                          </Cell>
                          <Cell>
                            <Text
                              fontSize={FontSize.Large}
                              color={
                                report.checkOutTime == null ||
                                isWithinXMinutes(
                                  new Date(report.endTime),
                                  new Date(report.checkOutTime),
                                  15
                                )
                                  ? undefined
                                  : Color.Destructive
                              }
                            >
                              {report.checkOutTime
                                ? formatTime(report.checkOutTime)
                                : "-"}
                            </Text>
                          </Cell>
                        </>
                      )}
                      <Cell>
                        <Text fontSize={FontSize.Large}>{report.gigRole}</Text>
                      </Cell>
                      <Cell>
                        {report.status == TimeReportStatus.CLOSED && (
                          <CustomIcon
                            name={"checkmark"}
                            color={Color.BurntSienna}
                            padding="5px"
                          />
                        )}
                        {report.status == TimeReportStatus.REJECTED && (
                          <CustomIcon
                            name={"cross"}
                            color={Color.Destructive}
                            padding="5px"
                          />
                        )}
                      </Cell>
                    </React.Fragment>
                  );
                }
                return null;
              });
            }
          )}
      </PersonnelCarrierGridBody>
    );
  };

  const renderPersonnelCarrierTableContent = () => {
    return (
      <>
        {monthsDates.map((date) => {
          const shouldDisplay = shouldDisplayDate(date, {
            showTodaysGigs,
            showOnlyDaysWithGigs,
            currentDate,
            checkForFilter,
            noTimeReports,
          });

          return shouldDisplay ? (
            <WorkerTimeReportContainer key={date}>
              <H4>{date}</H4>
              <PersonnelCarrierGrid haveStampClockModule={haveStampClockModule}>
                <HeadCell>
                  <Text
                    fontSize={FontSize.Large}
                    fontFamily={FontFamily.MontserratSemiBold}
                  >
                    {t("CompanyTimeReport.WorkerName")}
                  </Text>
                </HeadCell>
                <HeadCell>
                  <Text
                    fontSize={FontSize.Large}
                    fontFamily={FontFamily.MontserratSemiBold}
                  >
                    {t("CompanyTimeReport.SocialSecurityNumber")}
                  </Text>
                </HeadCell>
                <HeadCell>
                  <Text
                    fontSize={FontSize.Large}
                    fontFamily={FontFamily.MontserratSemiBold}
                  >
                    {t("CompanyTimeReport.StartTime")}
                  </Text>
                </HeadCell>
                <HeadCell>
                  <Text
                    fontSize={FontSize.Large}
                    fontFamily={FontFamily.MontserratSemiBold}
                  >
                    {t("CompanyTimeReport.EndTime")}
                  </Text>
                </HeadCell>
                <HeadCell>
                  <Text
                    fontSize={FontSize.Large}
                    fontFamily={FontFamily.MontserratSemiBold}
                  >
                    {t("CompanyTimeReport.Break")}
                  </Text>
                </HeadCell>
                {haveStampClockModule && (
                  <>
                    <HeadCell>
                      <Text
                        fontSize={FontSize.Large}
                        fontFamily={FontFamily.MontserratSemiBold}
                      >
                        {t("CompanyTimeReport.CheckIn")}
                      </Text>
                    </HeadCell>
                    <HeadCell>
                      <Text
                        fontSize={FontSize.Large}
                        fontFamily={FontFamily.MontserratSemiBold}
                      >
                        {t("CompanyTimeReport.CheckOut")}
                      </Text>
                    </HeadCell>
                  </>
                )}
                <HeadCell>
                  <Text
                    fontSize={FontSize.Large}
                    fontFamily={FontFamily.MontserratSemiBold}
                  >
                    {t("CompanyTimeReport.GigName")}
                  </Text>
                </HeadCell>
                <HeadCell></HeadCell>
              </PersonnelCarrierGrid>
              {renderPersonnelCarrierRow(date)}
            </WorkerTimeReportContainer>
          ) : (
            <React.Fragment key={date}></React.Fragment>
          );
        })}
      </>
    );
  };

  return (
    <>
      <PersonnelCarrierWrapper>
        <Text color={Color.White}>
          {`${t("CompanyTimeReport.CompanyName")}: ${
            companyState.company?.companyName
          }`}
        </Text>
        <Text color={Color.White} fontSize={FontSize.Small}>
          {`${t("CompanyTimeReport.OrganizationNumber")}: ${
            companyState.company?.registrationNumber
          }`}
        </Text>
      </PersonnelCarrierWrapper>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <MonthSelectContainer>
          <ArrowSign
            onClick={() => {
              setCurrentDate((prevState: any) => prevState.minusMonths(1));
            }}
          >
            &#60;
          </ArrowSign>
          <Text
            fontSize={FontSize.H4}
            fontFamily={FontFamily.MontserratSemiBold}
            style={{ margin: 0 }}
          >
            {`${t(`CompanyTimeReport.${chosenMonth}`)} ${currentDate.year()}`}
          </Text>
          <ArrowSign
            onClick={() => {
              setCurrentDate((prevState: any) => prevState.plusMonths(1));
            }}
          >
            &#62;
          </ArrowSign>
        </MonthSelectContainer>
      </div>
      <IconContainer>
        <CustomIcon
          name="filter"
          size="28px"
          color={Color.MidnightBlue}
          onClick={() => setShowFilterModal(true)}
        />
      </IconContainer>
      {noTimeReports && (
        <Text
          fontSize={FontSize.Large}
          fontFamily={FontFamily.MontserratSemiBold}
          align="center"
        >
          {t("CompanyTimeReport.NoReports")}
        </Text>
      )}
      {renderPersonnelCarrierTableContent()}
      {checkIfThereAreRejectedOrClosedTimeReports(TimeReportStatus.CLOSED) && (
        <TextRowPersonnelCarrier>
          <CustomIcon
            name={"checkmark"}
            color={Color.BurntSienna}
            padding="5px"
            marginTop="8px"
          />
          <Text>{t("CompanyTimeReport.TimeReportIsClosedAndApproved")}</Text>
        </TextRowPersonnelCarrier>
      )}
      {checkIfThereAreRejectedOrClosedTimeReports(
        TimeReportStatus.REJECTED
      ) && (
        <TextRowPersonnelCarrier>
          <CustomIcon
            name={"cross"}
            color={Color.Destructive}
            padding="5px"
            marginTop="8px"
          />
          <Text>{t("CompanyTimeReport.TimeReportIsRejected")}</Text>
        </TextRowPersonnelCarrier>
      )}
      <PersonnelCarrierFilterModal
        showFilterModal={showFilterModal}
        setShowFilterModal={setShowFilterModal}
        setShowOnlyDaysWithGigs={setShowOnlyDaysWithGigs}
        setShowTodaysGigs={setShowTodaysGigs}
        setShowTodaysGigsMarked={setShowTodaysGigsMarked}
        showTodaysGigsMarked={showTodaysGigsMarked}
        showOnlyDaysWithGigsMarked={showOnlyDaysWithGigsMarked}
        setShowOnlyDaysWithGigsMarked={setShowOnlyDaysWithGigsMarked}
      />

      {workerModalOpen && worker && (
        <WorkerDetail
          worker={worker}
          setWorkerModalOpen={setWorkerModalOpen}
          workerModalOpen={workerModalOpen}
        />
      )}
    </>
  );
};
